/* istanbul ignore file */

import {
  MultiselectSingleEntreeTypeBuilderMode,
  SelectableMenuItem,
  SelectableVariantMenuItem,
  ItemCardCustomization,
  SelectableMenuItemSource,
  OrderMealRequest,
  MenuItemCategory,
  MealBuilderItemSection,
} from '@/models';
import { AbstractMealBuilderItemVariantService } from '../../meal-builder-variant.service';
import { makeUniqueValue, getVariantsItemSectionTitle } from '@/helpers';

export class AppMultiselectSingleEntreeTypeMealBuilderService extends AbstractMealBuilderItemVariantService<MultiselectSingleEntreeTypeBuilderMode> {
  selectVariantSafe(
    builderMode: MultiselectSingleEntreeTypeBuilderMode,
    chosenVariant: SelectableVariantMenuItem,
    currentVariant: SelectableVariantMenuItem | null,
    allVariants: SelectableVariantMenuItem[],
    args: any,
    shouldTrackAnalytics?: boolean,
  ): void {
    // Make sure the selected variant is compatible with the rest of our variants
    if (currentVariant && !this.itemSelector.isDeselecting(chosenVariant, args)) {
      if (!this.areVariantsCompatible(currentVariant, chosenVariant)) {
        chosenVariant.message = makeUniqueValue(`(needs messaging for when user tries to select incompatible variant)`);
        throw new Error(`(needs messaging for when user tries to select incompatible variant)`);
      }
    }

    this.itemSelector.selectItemSafe(chosenVariant, builderMode.entree.virtualItemSection, currentVariant, args, shouldTrackAnalytics);

    // Handle "selected variant" selection (we have to choose a single variant to anchor content selection to)
    if (!this.itemSelector.isDeselecting(chosenVariant, args) && !currentVariant) {
      this.setSelectedVariant(chosenVariant);
    } else if (currentVariant && currentVariant == chosenVariant && this.itemSelector.isDeselecting(chosenVariant, args)) {
      this.setSelectedVariant(null);
    }
  }

  toggleVariantCustomizationSafe(
    builderMode: MultiselectSingleEntreeTypeBuilderMode,
    variant: SelectableMenuItem,
    allVariants: SelectableVariantMenuItem[],
    customization: ItemCardCustomization,
    shouldTrackAnalytics?: boolean
  ): void {
    // This is a no-op for this subclass
  }

  getVariants(builderMode: MultiselectSingleEntreeTypeBuilderMode): SelectableVariantMenuItem[] {
    return builderMode.entree.variants;
  }

  getVariantsItemSectionTitle(builderMode: MultiselectSingleEntreeTypeBuilderMode): string {
    return getVariantsItemSectionTitle(this.store.state.menu.metadata);
  }

  getVariantsItemSectionSubtitle(mode: MultiselectSingleEntreeTypeBuilderMode, variant: SelectableVariantMenuItem): string | undefined {
    return `Choose up to ${mode.entree.virtualItemSection.maxQuantity}.`;
  }

  getDefaultVariant(builderMode: MultiselectSingleEntreeTypeBuilderMode): SelectableVariantMenuItem | undefined {
    return builderMode.entree.variants[0];
  }

  buildMealRequest(builderMode: MultiselectSingleEntreeTypeBuilderMode, items: SelectableMenuItem[], mealName: string) {
    const variants = items.filter(i => i.itemSource == SelectableMenuItemSource.Variant) as SelectableVariantMenuItem[];

    // The "master" variant is the first variant we find with selected contents -- its contents will be replicated for all other variants
    const masterVariant = variants.find(v => !!v.contents.find(c => this.itemSelector.isItemSelected(c)));
    const contents = ((masterVariant && masterVariant.contents) || [])
      .filter(i => i.itemSource == SelectableMenuItemSource.VariantContents && this.itemSelector.isItemSelected(i))
      .map(i => this.selectableItemToOrderMealItem(i));

    const nonVariants = items.filter(i => i.itemSource == SelectableMenuItemSource.NonVariant);

    const meal: OrderMealRequest = {
      meal: {
        mealName,
        entrees: variants.map(variant => {
          return {
            menuItemId: variant.item.itemId,
            menuItemName: variant.item.itemName,
            quantity: this.itemSelector.getItemQuantity(variant),
            contents,
          };
        }),
        sides: nonVariants.filter(i => i.item.itemCategory == MenuItemCategory.Side).map(i => this.selectableItemToOrderMealItem(i)),
        drinks: nonVariants.filter(i => i.item.itemCategory == MenuItemCategory.Drink).map(i => this.selectableItemToOrderMealItem(i)),
      },
    };

    return meal;
  }

  getAdditionalItemSections(builderMode: MultiselectSingleEntreeTypeBuilderMode) {
    const sidesAndDrinks: MealBuilderItemSection[] = [
      {
        id: 'Side',
        title: 'Side',
        hidden: false,
        showSelectNone: false,
        items: builderMode.sides,
        maxCustomizations: null,
        minQuantity: null,
        maxQuantity: null,
      },
      {
        id: 'Drink',
        title: 'Drink',
        hidden: false,
        showSelectNone: false,
        items: builderMode.drinks,
        maxCustomizations: null,
        minQuantity: null,
        maxQuantity: null,
      },
    ];

    return sidesAndDrinks;
  }

  getEffectiveItemPrice(builderMode: MultiselectSingleEntreeTypeBuilderMode, item: SelectableMenuItem, variant: SelectableVariantMenuItem) {
    const price = this.menu.getItemPrice(item.item);

    if (item.itemSource != SelectableMenuItemSource.VariantContents) {
      return {
        price,
        formattedPrice: this.utils.formatPrice(price),
      };
    }

    const numVariantsSelected = this.itemSelector.getEffectiveItemSectionQuantity(builderMode.entree.virtualItemSection);
    const effectivePrice = price * numVariantsSelected;

    return {
      price: effectivePrice,
      formattedPrice: price ? `${this.utils.formatPrice(price)} x${numVariantsSelected} (${this.utils.formatPrice(effectivePrice)})` : '',
    };
  }

  private areVariantsCompatible(left: SelectableVariantMenuItem, right: SelectableVariantMenuItem) {
    return left.contents.every(li =>
      !!right.contents.find(ri =>
        ri.item.itemId == li.item.itemId &&
        this.menu.getItemPrice(ri.item) == this.menu.getItemPrice(li.item)
      )
    );
  }
}
